import React, { useCallback, useState } from 'react';
import { Box } from '@saviynt/design-system';

import { ActionsPanel } from './components/ActionsPanel';
import { FindingsList } from './components/FindingsList';
import { InsightTimeline } from './components/InsightTimeline';
import { KillChainTimeline } from './components/KillChainTimeline';
import { SessionAreaChart } from './components/SessionAreaChart';
import { StatCards } from './components/StatCards';
import type {
  Finding,
  FindingStatus,
  InsightDashboardData,
  KillChainPhase,
  SeverityLevel,
} from './data-layer/insightDashboardTypes';

import './InsightDashboard.css';

interface InsightDashboardProps {
  data: InsightDashboardData;
}

/**
 * Renders the Insight Dashboard page, displaying session threat landscape,
 * stat cards, attack graph, findings list, and immediate actions panel.
 * @param props - Component props.
 * @param props.data - The full insight dashboard data including findings, log entries, and actions.
 * @returns The rendered insight dashboard.
 */
function InsightDashboard({ data }: InsightDashboardProps) {
  const [phaseFilter, setPhaseFilter] = useState<KillChainPhase | 'ALL'>('ALL');
  const [findingsState, setFindingsState] = useState<Finding[]>(data.findings);
  const [selectedSession] = useState<string>('all');
  const [actions, setActions] = useState(data.immediateActions);
  const [severityFilter, setSeverityFilter] = useState<SeverityLevel | 'ALL'>(
    'CRITICAL'
  );
  const [highlightedFindingId, setHighlightedFindingId] = useState<
    number | null
  >(null);
  const [isGraphVisible, setIsGraphVisible] = useState(false);
  const [sessionColorMap, setSessionColorMap] = useState<Record<
    string,
    string
  > | null>(null);
  const [sliceVisibleFindingIds, setSliceVisibleFindingIds] = useState<
    number[] | null
  >(null);
  const [sliceTimeRange, setSliceTimeRange] = useState<{
    startEpoch: number;
    endEpoch: number;
  } | null>(null);
  const findingsRef = React.useRef<HTMLDivElement>(null);

  const handleStatusChange = useCallback(
    (id: number, status: FindingStatus) => {
      setFindingsState((prev) =>
        prev.map((f) => (f.id === id ? { ...f, status } : f))
      );
    },
    []
  );

  const handleToggleAction = useCallback((id: number) => {
    setActions((prev) =>
      prev.map((a) => (a.id === id ? { ...a, completed: !a.completed } : a))
    );
  }, []);

  const handleIncidentClick = useCallback(
    (
      severity: 'CRITICAL' | 'HIGH' | 'MEDIUM',
      findingId: number,
      colorMap?: Record<string, string>
    ) => {
      setSeverityFilter(severity);
      setPhaseFilter('ALL');
      setSessionColorMap(colorMap ?? null);
      setHighlightedFindingId(findingId);
      // Scroll to the specific finding card after render
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-finding-id="${findingId}"]`);

        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          findingsRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
      // Clear highlight after 3 seconds
      setTimeout(() => setHighlightedFindingId(null), 3000);
    },
    []
  );

  return (
    <Box className='Sod-InsightDashboard'>
      {/* Session Threat Landscape — Stacked Area Chart */}
      <SessionAreaChart
        logEntries={data.logEntries}
        findings={findingsState}
        onIncidentClick={handleIncidentClick}
        onSessionColorMapReady={setSessionColorMap}
        onSliceChange={(timeRange, findingIds) => {
          setSliceVisibleFindingIds(findingIds ?? null);
          setSliceTimeRange(timeRange);
        }}
      />

      {/* Stat Cards */}
      <StatCards
        summary={data.reportSummary}
        exploitCategories={data.exploitCategories}
      />

      {/* Kill Chain Analysis — toggled from Findings header */}
      {isGraphVisible && (
        <KillChainTimeline
          findings={findingsState}
          logEntries={data.logEntries}
          killChainTimeline={data.killChainTimeline}
          onFindingClick={(finding) => {
            handleIncidentClick(
              finding.severity as 'CRITICAL' | 'HIGH' | 'MEDIUM',
              finding.id
            );
          }}
        />
      )}

      {/* Findings */}
      <div ref={findingsRef}>
        <FindingsList
          findings={findingsState}
          logEntries={data.logEntries}
          phaseFilter={phaseFilter}
          onPhaseChange={setPhaseFilter}
          onStatusChange={handleStatusChange}
          selectedSession={selectedSession}
          severityFilter={severityFilter}
          onSeverityFilterChange={setSeverityFilter}
          highlightedFindingId={highlightedFindingId}
          autoExpandFindingId={highlightedFindingId}
          sessionColorMap={sessionColorMap}
          sliceVisibleFindingIds={sliceVisibleFindingIds}
          isGraphVisible={isGraphVisible}
          onToggleGraph={() => setIsGraphVisible((prev) => !prev)}
        />
      </div>

      {/* Bottom row: Actions + Log Details side by side */}
      <Box className='Sod-InsightDashboard-bottomRow'>
        <ActionsPanel actions={actions} onToggleAction={handleToggleAction} />
        <InsightTimeline
          logEntries={data.logEntries}
          findings={findingsState}
          sliceTimeRange={sliceTimeRange}
          phaseFilter={phaseFilter}
        />
      </Box>
    </Box>
  );
}

export default InsightDashboard;
